import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Card,
    CardContent,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    CircularProgress,
    Alert,
    TextField,
    TablePagination,
} from "@mui/material";
import {
    Visibility as VisibilityIcon,
    Close as CloseIcon,
} from "@mui/icons-material";
import i18n from "i18next";
import { useSelector } from "react-redux";
import { getDatabase, ref, get } from "firebase/database";
import { FirebaseConfig } from "../../config/FirebaseConfig";
import { initializeApp, getApps } from "firebase/app";
import appConfig from "functions/config";

// Initialize Firebase if not already initialized
let app;
if (!getApps().length) {
    app = initializeApp(FirebaseConfig);
} else {
    app = getApps()[0];
}
const database = getDatabase(app);

const AiBooking = () => {
    const { t } = i18n;
    const [bookingRecords, setBookingRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [conversationMessages, setConversationMessages] = useState([]);
    const [conversationLoading, setConversationLoading] = useState(false);
    const [conversationError, setConversationError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    // Get users data from Redux store
    const userdata = useSelector((state) => state.usersdata);

    // Normalize phone numbers for matching - pure function, no need to memoize
    const normalizePhone = (phone) => {
        if (!phone) return "";
        return phone.toString().replace(/^\+?0*/, "");
    };

    // Find user by phone number with flexible matching - memoized to prevent recreation
    const findUserByPhone = useCallback(
        (phoneNumber) => {
            if (!phoneNumber || !userdata.users) return null;

            return userdata.users.find((user) => {
                if (!user.mobile) return false;

                const userPhone = normalizePhone(user.mobile);
                const logPhone = normalizePhone(phoneNumber);

                return (
                    userPhone.includes(logPhone) || logPhone.includes(userPhone)
                );
            });
        },
        [userdata.users]
    );

    // Get booking data from Firebase - memoized to prevent recreation
    const getBookingFromFirebase = useCallback(async (bookingId) => {
        try {
            const bookingRef = ref(database, `bookings/${bookingId}`);
            const snapshot = await get(bookingRef);

            if (snapshot.exists()) {
                return snapshot.val();
            }
            return null;
        } catch (error) {
            console.error("Error fetching booking from Firebase:", error);
            return null;
        }
    }, []); // No dependencies since it only uses external services

    // Fetch booking logs from API - memoized to prevent unnecessary re-renders
    const fetchBookingLogs = useCallback(
        async (enrichWithUserData = true) => {
            try {
                setLoading(true);
                setError(null);

                const offset = page * rowsPerPage;
                const params = new URLSearchParams({
                    limit: rowsPerPage.toString(),
                    offset: offset.toString(),
                    ...(searchTerm && { search: searchTerm }),
                });

                const response = await fetch(
                    `${appConfig.api_endpoints.whatsapp_ai_url}/chat-history/list-booking-logs?${params}`,
                    {
                        method: "GET",
                        headers: {
                            "x-key": "a5af540687e5f73a00ad4a6a7545b5cc1ba1fa4c",
                            "Content-Type": "application/json",
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const responseData = await response.json();
                console.log("Full API response:", responseData);

                // Handle both array and object responses
                let bookingLogs = [];
                if (Array.isArray(responseData)) {
                    bookingLogs = responseData;
                    setTotalRecords(responseData.length);
                } else if (
                    responseData.data &&
                    Array.isArray(responseData.data)
                ) {
                    bookingLogs = responseData.data;
                    // Use totalAllRecord for total count, fallback to total if not available
                    setTotalRecords(
                        responseData.totalAllRecord || responseData.total || 0
                    );
                } else {
                    console.error(
                        "Unexpected API response format:",
                        responseData
                    );
                    setTotalRecords(0);
                }
                console.log("Booking logs from API:", bookingLogs);

                // Enrich logs with Firebase booking data and user information
                const enrichedRecords = [];

                if (Array.isArray(bookingLogs)) {
                    for (const log of bookingLogs) {
                        // Get booking data from Firebase
                        const bookingData = await getBookingFromFirebase(
                            log.booking_id
                        );

                        // Find user by phone number - only if enrichment is requested and userdata is available
                        let user = null;
                        if (
                            enrichWithUserData &&
                            userdata.users &&
                            userdata.users.length > 0
                        ) {
                            user = log.number_user
                                ? findUserByPhone(log.number_user)
                                : null;
                        }

                        // Find driver if booking data exists - only if enrichment is requested and userdata is available
                        let driver = null;
                        if (
                            enrichWithUserData &&
                            bookingData &&
                            bookingData.driver_id &&
                            userdata.users &&
                            userdata.users.length > 0
                        ) {
                            driver = userdata.users.find(
                                (u) => u.id === bookingData.driver_id
                            );
                        }

                        // Always add the record, even if user/driver not found
                        enrichedRecords.push({
                            bookingId: log.logNumber || log.booking_id,
                            originalBookingId: log.booking_id,
                            referenceId:
                                bookingData?.reference || log.booking_id,
                            customerName: user
                                ? `${user.firstName || ""} ${
                                      user.lastName || ""
                                  }`.trim() || "Unknown User"
                                : log.number_user
                                ? `User ${log.number_user}`
                                : "-",
                            customerEmail: user
                                ? user.email || "No email"
                                : "-",
                            customerPhone: log.number_user || "-",
                            driverName: driver
                                ? `${driver.firstName || ""} ${
                                      driver.lastName || ""
                                  }`.trim() || "Unknown Driver"
                                : "-",
                            status: bookingData?.status || "unknown",
                            date: log.created_at,
                            bookingData: bookingData,
                            hasUser: !!user,
                            hasBookingData: !!bookingData,
                            conversation: [], // Will be populated when viewing conversation
                        });
                    }
                }

                console.log("Enriched booking records:", enrichedRecords);
                setBookingRecords(enrichedRecords);
            } catch (err) {
                console.error("Error fetching booking logs:", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [page, rowsPerPage, searchTerm, getBookingFromFirebase]
        // userdata.users and findUserByPhone intentionally excluded to prevent infinite loop
        // They are accessed via the enrichWithUserData parameter
    );

    // Track if user data is available - memoized to prevent unnecessary re-renders
    const hasUserData = useMemo(() => {
        return userdata.users && userdata.users.length > 0;
    }, [userdata.users]);

    // Fetch booking logs when pagination or search changes
    useEffect(() => {
        console.log(
            `📄 Fetching page ${
                page + 1
            } with ${rowsPerPage} records per page, search: "${searchTerm}"`
        );
        fetchBookingLogs(hasUserData);
    }, [fetchBookingLogs, hasUserData, page, rowsPerPage, searchTerm]); // Include all dependencies

    // Fetch conversation history from API - memoized to prevent recreation
    const fetchConversationHistory = useCallback(async (record) => {
        try {
            setConversationLoading(true);
            setConversationError(null);
            setConversationMessages([]);

            const response = await fetch(
                `${appConfig.api_endpoints.whatsapp_ai_url}/chat-history/history`,
                {
                    method: "POST",
                    headers: {
                        "x-key": "a5af540687e5f73a00ad4a6a7545b5cc1ba1fa4c",
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        number_user: record.customerPhone,
                        service: "booking",
                        limit: 20,
                        offset: 0,
                        booking_id: record.originalBookingId || "",
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const messages = await response.json();
            console.log("Conversation messages from API:", messages);

            // Transform API response to match our UI format
            const transformedMessages = messages.map((msg) => ({
                id: msg.id,
                speaker: msg.type_message, // 'user' or 'ai'
                text: msg.message_user,
                timestamp: new Date(msg.created_at).toLocaleTimeString(
                    "es-ES",
                    {
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                    }
                ),
                fullDate: msg.created_at,
                bookingId: msg.booking_id,
            }));

            setConversationMessages(transformedMessages);
        } catch (err) {
            console.error("Error fetching conversation history:", err);
            setConversationError(err.message);
        } finally {
            setConversationLoading(false);
        }
    }, []); // No dependencies since it only uses parameters and external API

    const handleViewConversation = (record) => {
        setSelectedRecord(record);
        setDialogOpen(true);

        // Fetch conversation history if user phone is available
        if (record.customerPhone && record.customerPhone !== "-") {
            fetchConversationHistory(record);
        } else {
            setConversationMessages([]);
            setConversationError("No phone number available for this booking");
        }
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedRecord(null);
        setConversationMessages([]);
        setConversationError(null);
    };

    const handleRefresh = useCallback(() => {
        // Always fetch with user enrichment on manual refresh
        fetchBookingLogs(true);
    }, [fetchBookingLogs]);

    // Search handling with debounce - memoized to prevent recreation
    const handleSearchChange = useCallback((e) => {
        setSearchTerm(e.target.value);
    }, []);

    // Pagination handlers - memoized to prevent recreation
    const handleChangePage = useCallback((event, newPage) => {
        setPage(newPage);
    }, []);

    const handleChangeRowsPerPage = useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }, []);

    const getStatusColor = useCallback((status) => {
        switch (status) {
            case "COMPLETED":
            case "completed":
                return "success";
            case "CANCELLED":
            case "cancelled":
                return "error";
            case "ACCEPTED":
            case "STARTED":
            case "ARRIVED":
            case "PICKED":
            case "in_progress":
                return "primary";
            case "PENDING":
            case "pending":
                return "warning";
            case "unknown":
                return "default";
            default:
                return "default";
        }
    }, []);

    const getStatusText = useCallback((status) => {
        switch (status) {
            case "COMPLETED":
            case "completed":
                return "Completado";
            case "CANCELLED":
            case "cancelled":
                return "Cancelado";
            case "ACCEPTED":
                return "Aceptado";
            case "STARTED":
                return "Iniciado";
            case "ARRIVED":
                return "Llegó";
            case "PICKED":
                return "Recogido";
            case "in_progress":
                return "En Progreso";
            case "PENDING":
            case "pending":
                return "Pendiente";
            case "unknown":
                return "Desconocido";
            default:
                return status || "N/A";
        }
    }, []);

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" sx={{ mb: 1, fontWeight: "bold" }}>
                    {t("ai_booking_records")}
                </Typography>
                <Typography variant="body1" color="textSecondary">
                    {t("ai_booking_description")}
                </Typography>
            </Box>

            {/* Booking Records Table */}
            <Card>
                <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2,
                            gap: 2,
                            flexWrap: "wrap",
                        }}
                    >
                        <Typography variant="h6">
                            {t("booking_records")} ({totalRecords})
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <TextField
                                size="small"
                                placeholder={t("search") || "Search"}
                                value={searchTerm}
                                onChange={handleSearchChange}
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleRefresh}
                                disabled={loading}
                            >
                                {loading
                                    ? t("loading") || "Loading..."
                                    : t("refresh") || "Refresh"}
                            </Button>
                        </Box>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            Error loading booking records: {error}
                            <Button
                                size="small"
                                onClick={handleRefresh}
                                sx={{ ml: 2 }}
                            >
                                Retry
                            </Button>
                        </Alert>
                    )}

                    {loading ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                py: 4,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : bookingRecords.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 4 }}>
                            <Typography>
                                {t("no_booking_records_found")}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <TableContainer component={Paper} elevation={0}>
                                <Table>
                                    <TableHead>
                                        <TableRow
                                            sx={{ backgroundColor: "#f8f9fa" }}
                                        >
                                            <TableCell
                                                sx={{ fontWeight: "bold" }}
                                            >
                                                {t("booking_id")}
                                            </TableCell>
                                            <TableCell
                                                sx={{ fontWeight: "bold" }}
                                            >
                                                {t("reference_id")}
                                            </TableCell>
                                            <TableCell
                                                sx={{ fontWeight: "bold" }}
                                            >
                                                {t("customer")}
                                            </TableCell>
                                            <TableCell
                                                sx={{ fontWeight: "bold" }}
                                            >
                                                {t("driver")}
                                            </TableCell>
                                            <TableCell
                                                sx={{ fontWeight: "bold" }}
                                            >
                                                {t("status")}
                                            </TableCell>
                                            <TableCell
                                                sx={{ fontWeight: "bold" }}
                                            >
                                                {t("actions")}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {bookingRecords.map((record) => (
                                            <TableRow
                                                key={record.bookingId}
                                                hover
                                            >
                                                <TableCell
                                                    sx={{ fontWeight: "bold" }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            gap: 1,
                                                        }}
                                                    >
                                                        {record.bookingId}
                                                        {!record.hasBookingData && (
                                                            <Chip
                                                                label="No Booking Data"
                                                                size="small"
                                                                color="warning"
                                                                sx={{
                                                                    fontSize:
                                                                        "0.7rem",
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell
                                                    sx={{ color: "#6c757d" }}
                                                >
                                                    {record.referenceId}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        sx={{
                                                            color: record.hasUser
                                                                ? "inherit"
                                                                : "text.secondary",
                                                            fontStyle:
                                                                record.hasUser
                                                                    ? "normal"
                                                                    : "italic",
                                                        }}
                                                    >
                                                        {record.customerName}
                                                    </Typography>
                                                    {record.customerPhone !==
                                                        "-" && (
                                                        <Typography
                                                            variant="caption"
                                                            color="textSecondary"
                                                        >
                                                            {
                                                                record.customerPhone
                                                            }
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        sx={{
                                                            fontStyle:
                                                                record.driverName ===
                                                                "-"
                                                                    ? "italic"
                                                                    : "normal",
                                                            color:
                                                                record.driverName ===
                                                                "-"
                                                                    ? "text.secondary"
                                                                    : "inherit",
                                                        }}
                                                    >
                                                        {record.driverName}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={getStatusText(
                                                            record.status
                                                        )}
                                                        color={getStatusColor(
                                                            record.status
                                                        )}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={
                                                            <VisibilityIcon />
                                                        }
                                                        onClick={() =>
                                                            handleViewConversation(
                                                                record
                                                            )
                                                        }
                                                        sx={{
                                                            minWidth: "auto",
                                                            whiteSpace:
                                                                "nowrap",
                                                        }}
                                                    >
                                                        {t("view_conversation")}
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={totalRecords}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[5, 10, 25, 50]}
                            />
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Conversation Detail Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Typography variant="h6">
                            {t("conversation")} - {selectedRecord?.customerName}{" "}
                            (#{selectedRecord?.bookingId})
                        </Typography>
                        <IconButton onClick={handleCloseDialog} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" color="textSecondary">
                            <strong>Phone:</strong>{" "}
                            {selectedRecord?.customerPhone}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            <strong>Email:</strong>{" "}
                            {selectedRecord?.customerEmail}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            <strong>Booking ID:</strong>{" "}
                            {selectedRecord?.originalBookingId}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            <strong>Date:</strong>{" "}
                            {selectedRecord?.date
                                ? new Date(selectedRecord.date).toLocaleString(
                                      "es-ES"
                                  )
                                : "N/A"}
                        </Typography>
                    </Box>

                    {conversationError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            Error loading conversation: {conversationError}
                            <Button
                                size="small"
                                onClick={() =>
                                    selectedRecord &&
                                    fetchConversationHistory(selectedRecord)
                                }
                                sx={{ ml: 2 }}
                            >
                                Retry
                            </Button>
                        </Alert>
                    )}

                    {conversationLoading ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                py: 4,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                fontFamily: '"Courier New", Courier, monospace',
                                fontSize: "14px",
                                lineHeight: 1.6,
                                whiteSpace: "pre-wrap",
                                backgroundColor: "#f8f9fa",
                                padding: 2,
                                borderRadius: 1,
                                border: "1px solid #e9ecef",
                            }}
                        >
                            {conversationMessages.length > 0 ? (
                                conversationMessages.map((item, index) => (
                                    <Box
                                        key={item.id || index}
                                        sx={{ mb: 1.5 }}
                                    >
                                        <Typography
                                            component="span"
                                            sx={{
                                                fontWeight: "bold",
                                                color: "#495057",
                                            }}
                                        >
                                            {item.speaker === "user"
                                                ? selectedRecord?.customerName
                                                : "AI"}
                                            :
                                        </Typography>
                                        <Typography
                                            component="span"
                                            sx={{ ml: 1, color: "#212529" }}
                                        >
                                            {item.text}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            sx={{ display: "block", mt: 0.5 }}
                                        >
                                            {new Date(
                                                item.fullDate
                                            ).toLocaleDateString("es-ES")}{" "}
                                            {item.timestamp}
                                            {item.bookingId && (
                                                <span
                                                    style={{
                                                        marginLeft: "8px",
                                                        fontWeight: "bold",
                                                    }}
                                                >
                                                    | Booking: {item.bookingId}
                                                </span>
                                            )}
                                        </Typography>
                                    </Box>
                                ))
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ textAlign: "center", py: 2 }}
                                >
                                    No conversation messages found for this
                                    booking.
                                </Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions
                    sx={{ px: 3, pb: 3, pt: 1, justifyContent: "flex-end" }}
                >
                    <Button onClick={handleCloseDialog} variant="contained">
                        {t("close")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AiBooking;